import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Sun, Moon } from 'lucide-react';
import dayjs from 'dayjs';
import { api, apiErrorMessage } from '../apiClient';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import EventCard from '../components/EventCard';
import BannerCarousel from '../components/BannerCarousel';
import NotificationBell from '../components/NotificationBell';

const SECTIONS = [
  { key: 'featured', title: 'Featured', subtitle: 'Handpicked for you' },
  { key: 'trending', title: 'Trending Now', subtitle: 'What everyone is talking about' },
  { key: 'upcoming', title: 'Upcoming', subtitle: 'Book your calendar' },
  { key: 'near_you', title: 'Near You', subtitle: 'In your city' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user, isAuthed } = useAuth();
  const { isDark, setMode } = useTheme();
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const feedMode = isDark ? 'night' : 'day';

  const load = useCallback(async () => {
    try {
      const [catsRes, ...secRes] = await Promise.all([
        api.get('/categories'),
        ...SECTIONS.map((s) => api.get('/events', { params: { section: s.key, limit: 20, mode: feedMode } })),
      ]);
      setCategories(catsRes.data);
      const map = {};
      SECTIONS.forEach((s, i) => { map[s.key] = secRes[i].data; });
      setData(map);
    } catch (e) {
      console.log('home load error', apiErrorMessage(e));
    }
  }, [feedMode]);

  useEffect(() => {
    (async () => { setLoading(true); await load(); setLoading(false); })();
  }, [load]);

  const toggleDayNight = useCallback(() => {
    setMode(isDark ? 'light' : 'dark');
  }, [isDark, setMode]);

  const tonight = useMemo(() => {
    if (!isDark) return [];
    const now = dayjs();
    const in24h = now.add(24, 'hour');
    const all = [];
    Object.values(data).forEach((arr) => { arr.forEach((e) => all.push(e)); });
    const seen = new Set();
    return all
      .filter((e) => {
        if (seen.has(e.id)) return false;
        const start = dayjs(e.start_datetime);
        if (!start.isValid() || start.isBefore(now) || start.isAfter(in24h)) return false;
        seen.add(e.id);
        return true;
      })
      .sort((a, b) => dayjs(a.start_datetime).valueOf() - dayjs(b.start_datetime).valueOf())
      .slice(0, 10);
  }, [data, isDark]);

  return (
    <div className="max-w-7xl mx-auto" data-testid="home-page">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex-1">
          <p className="text-xs font-semibold text-gray-500">
            {isAuthed ? `Hey, ${user?.full_name?.split(' ')[0] || 'there'} \ud83d\udc4b` : 'Discover'}
          </p>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">
            Find your next experience
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="home-daynight-toggle"
            onClick={toggleDayNight}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to night mode'}
            className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors ${
              isDark
                ? 'bg-pink-50 border-brand-magenta text-brand-magenta'
                : 'bg-white border-gray-100 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
          <NotificationBell />
          <button
            data-testid="home-search-btn"
            onClick={() => navigate('/search')}
            className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <Search className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>

      {/* Category Chips */}
      <div className="flex gap-2 px-6 py-2 overflow-x-auto no-scrollbar">
        {[{ id: 'all', name: 'All', slug: 'all' }, ...categories].map((c) => (
          <button
            key={c.id}
            data-testid={`category-chip-${c.slug}`}
            onClick={() => { if (c.slug !== 'all') navigate(`/search?category=${c.name}`); }}
            className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
              c.slug === 'all'
                ? 'bg-brand-magenta border-brand-magenta text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-brand-magenta" />
        </div>
      ) : (
        <>
          <BannerCarousel />

          {/* Tonight section (night mode only) */}
          {isDark && tonight.length > 0 && (
            <div className="mt-8" data-testid="home-section-tonight">
              <div className="px-6 mb-3">
                <h2 className="text-xl font-black text-brand-magenta tracking-tight">Tonight \ud83c\udf19</h2>
                <p className="text-xs text-gray-500 mt-0.5">Happening after sunset</p>
              </div>
              <div className="flex overflow-x-auto no-scrollbar px-6 pb-2">
                {tonight.map((item) => (
                  <EventCard key={`tonight-${item.id}`} event={item} variant="horizontal" testId={`event-card-tonight-${item.id}`} />
                ))}
              </div>
            </div>
          )}

          {SECTIONS.map((s) => {
            const items = data[s.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={s.key} className="mt-8" data-testid={`home-section-${s.key}`}>
                <div className="px-6 mb-3">
                  <h2 className="text-xl font-black text-gray-900 tracking-tight">{s.title}</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{s.subtitle}</p>
                </div>
                <div className="flex overflow-x-auto no-scrollbar px-6 pb-2">
                  {items.map((item) => (
                    <EventCard key={item.id} event={item} variant="horizontal" testId={`event-card-${s.key}-${item.id}`} />
                  ))}
                </div>
              </div>
            );
          })}
          <div className="h-8" />
        </>
      )}
    </div>
  );
}
